import type { StyleOptions } from '@aus-platform/cesium';
import type { SelectOption } from '@aus-platform/design-system';
import {
  GEOMETRY_TYPE,
  Line,
  Point,
  Polygon,
  Textbox,
} from '@aus-platform/cesium';
import { isNil } from 'lodash';
import type { WorkspaceLayer } from '../../../../shared';
import type { Feature, FeatureObj } from 'shared/api';
import { FeatureCountLabel, FeatureType } from 'shared/api';
import type { DrawingTools } from 'map-3d/shared/map-3d-tools';

/**
 * Returns true when the layer has textbox features.
 *
 * @param layer WorkspaceLayer
 */
export const layerHasTextboxFeatures = (layer: WorkspaceLayer): boolean =>
  Boolean(
    (layer.featuresCount ?? layer.properties?.featuresCount)?.[
      FeatureCountLabel.Textbox
    ],
  );

export const getEligibleLayerOptions = (
  layers: WorkspaceLayer[],
  featureType: FeatureCountLabel,
  requiredCount: number,
): SelectOption<WorkspaceLayer>[] =>
  layers
    .filter(
      (layer) =>
        layer.properties?.featuresCount?.[featureType] === requiredCount,
    )
    .map((layer) => ({ label: layer.name, value: layer }));

export const toggleFeatureVisibility = (
  feature: FeatureObj,
  drawingTools: DrawingTools,
) => {
  if (feature instanceof Polygon) {
    drawingTools.polygonTool.getPolygon(feature.id)?.toggleVisibility();
  } else if (feature instanceof Line) {
    drawingTools.lineTool.getLine(feature.id)?.toggleVisibility();
  } else if (feature instanceof Point) {
    drawingTools.pointTool.getPoint(feature.id)?.toggleVisibility();
  } else if (feature instanceof Textbox) {
    drawingTools.textboxTool.getTextbox(feature.id)?.toggleVisibility();
  }
};

export const getGeometryType = (featureType: FeatureType) => {
  switch (featureType) {
    case FeatureType.Polygon:
      return GEOMETRY_TYPE.POLYGON;
    case FeatureType.LineString:
      return GEOMETRY_TYPE.LINE;
    case FeatureType.Point:
      return GEOMETRY_TYPE.POINT;
    case FeatureType.Textbox:
      return GEOMETRY_TYPE.TEXT;
    default:
      return null;
  }
};

export const toggleFeaturesVisibility = (
  features: Feature[],
  drawingTools: DrawingTools,
) => {
  for (const feature of features) {
    if (feature.feature) {
      toggleFeatureVisibility(feature.feature, drawingTools);
    }
  }
};

export const removeFeature = (
  feature: FeatureObj,
  drawingTools: DrawingTools,
) => {
  if (feature instanceof Polygon) {
    drawingTools.polygonTool.deleteById(feature.id);
  } else if (feature instanceof Line) {
    drawingTools.lineTool.deleteById(feature.id);
  } else if (feature instanceof Point) {
    drawingTools.pointTool.deletePoint(feature.id);
  } else if (feature instanceof Textbox) {
    drawingTools.textboxTool.deleteById(feature.id);
  }
};

export const removeAllFeatures = (
  layer: WorkspaceLayer,
  drawingTools?: DrawingTools | null,
) => {
  const features = layer.features ?? [];
  if (drawingTools) {
    for (const feature of features) {
      if (feature.feature) {
        removeFeature(feature.feature, drawingTools);
      }
    }
  }
};

/**
 * Load a single feature for editing.
 * If the feature is already loaded, return it as-is.
 * If the feature is not loaded, import it and show vertices.
 * Keeps all previously loaded features with vertices visible to allow multi-feature editing.
 */
export const loadSingleFeature = (
  feature: Feature,
  layerId: string,
  drawingTools: DrawingTools,
  styleOptions: StyleOptions,
): Feature => {
  // If feature is already loaded, just return it (don't toggle).
  if (!isNil(feature.feature)) {
    return feature;
  }

  let featureObj: FeatureObj = null;

  switch (feature.type) {
    case FeatureType.Polygon: {
      const importedPolygons = drawingTools.polygonTool.importWKT(
        feature.geometry,
        feature.id,
        { layerId, isLayer: true },
        styleOptions.polygonStyleOptions?.label,
        styleOptions,
      );
      if (!isNil(importedPolygons)) {
        featureObj = importedPolygons[0];
        featureObj.showVertices = true;
      }
      break;
    }

    case FeatureType.LineString: {
      const importedLines = drawingTools.lineTool.importWKT(
        feature.geometry,
        feature.id,
        {
          layerId,
          isLayer: true,
        },
        styleOptions.lineStyleOptions?.label,
        styleOptions,
      );
      if (!isNil(importedLines)) {
        featureObj = importedLines[0];
        featureObj.showVertices = true;
      }
      break;
    }

    case FeatureType.Point: {
      const importedPoints = drawingTools.pointTool.importWKT(
        feature.geometry,
        feature.id,
        {
          layerId,
          isLayer: true,
        },
        styleOptions.pointStyleOptions?.label,
      );

      if (!isNil(importedPoints)) {
        featureObj = importedPoints[0];
      }
      break;
    }

    case FeatureType.Textbox: {
      if (feature?.properties?.text) {
        const importedTextboxes = drawingTools.textboxTool.importWKT(
          feature.geometry,
          feature.properties?.text,
          feature.id,
          {
            layerId,
            isLayer: true,
          },
          styleOptions,
        );
        if (!isNil(importedTextboxes)) {
          featureObj = importedTextboxes[0];
        }
      }
      break;
    }
  }

  return { ...feature, feature: featureObj };
};
